/**
 * Salvage Run mode: collect salvage, deliver to depot for points.
 * Pure functions — no side effects, no rendering.
 */

import type { PlanetState, PlayerState, CollectibleNode, GameState, ModeConfig } from '../types';
import { DEFAULT_SALVAGE_NODE_COUNT, SALVAGE_VALUE_RANGE, SALVAGE_WEIGHT_RANGE } from '../constants';
import { generateCollectibleNodes } from './collectibles';

/**
 * Initialize Salvage Run: generate salvage nodes, assign depot planets per team.
 */
export function initSalvageRun(
  planets: PlanetState[],
  players: PlayerState[],
  rng: { range: (min: number, max: number) => number; int: (min: number, max: number) => number },
  config: ModeConfig
): { collectibles: CollectibleNode[]; depotPlanets: Record<number, number> } {
  const nodeCount = config.salvageNodeCount ?? DEFAULT_SALVAGE_NODE_COUNT;

  const collectibles = generateCollectibleNodes(
    planets,
    'salvageNode',
    nodeCount,
    rng,
    SALVAGE_VALUE_RANGE,
    SALVAGE_WEIGHT_RANGE
  );

  // Assign depot planet per team (their starting planet)
  const depotPlanets: Record<number, number> = {};
  const seenTeams = new Set<number>();
  for (const p of players) {
    if (!seenTeams.has(p.teamIndex)) {
      depotPlanets[p.teamIndex] = p.planetIndex;
      seenTeams.add(p.teamIndex);
    }
  }

  return { collectibles, depotPlanets };
}

/**
 * Process a salvage delivery at the team's depot.
 * Returns updated state with incremented delivered value.
 */
export function processSalvageDelivery(
  state: GameState,
  playerId: number,
  carryableId: number
): GameState {
  const sv = state.modeState.salvage;
  if (!sv) return state;

  const carryable = state.carryables.find(c => c.id === carryableId);
  if (!carryable || carryable.carriedBy !== playerId) return state;

  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  const teamIndex = player.teamIndex;
  const value = carryable.value ?? 0;

  const existingValue = sv.deliveredValue[teamIndex] ?? 0;

  // Remove the carryable
  const updatedCarryables = state.carryables.filter(c => c.id !== carryableId);

  return {
    ...state,
    carryables: updatedCarryables,
    modeState: {
      ...state.modeState,
      salvage: {
        ...sv,
        deliveredValue: {
          ...sv.deliveredValue,
          [teamIndex]: existingValue + value,
        },
      },
    },
  };
}
